import { zodResolver } from "@hookform/resolvers/zod"
import { Box, Dialog, DialogContent, Divider, IconButton, Typography } from "@mui/material"
import { CloseOutlined, MailOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import React, { useMemo, useState } from "react"
import { FormProvider, useForm } from "react-hook-form"

import { CancelDialog } from "@/components/common/Modals"
import { FULL_LIST_LIMIT } from "@/constants/common"
import { SendDialogActions } from "@/screens/Pricing/components/dialogs/SendDialog/SendDialogActions"
import EmailTemplateForm from "@/screens/Pricing/components/forms/EmailTemplateForm"
import ReSendIndividualForm from "@/screens/Pricing/components/forms/ReSendIndividualForm"
import { RecipientType } from "@/screens/Pricing/constants"
import { DEFAULT_EMAIL_NOTE } from "@/screens/Pricing/constants/submitPriceChange"
import { useGetPriceChangePreview } from "@/screens/Pricing/hooks/useGetPriceChangePreview"
import { useGetPriceChangeRecipients } from "@/screens/Pricing/hooks/useGetPriceChangeRecepients"
import { useGetPriceChangeSubmission } from "@/screens/Pricing/hooks/useGetPriceChangeSubmission"
import { ReSendToIndividualSchema } from "@/screens/Pricing/schema/SendPriceChangeSchema"
import type { ReSendToIndividualSchemaFormValues } from "@/screens/Pricing/schema/SendPriceChangeSchema"
import { getPriceChangeGroupsCustomerIds } from "@/screens/Pricing/utils/priceChangeGroups"
import type { Option } from "@/types/form-fields"

interface ReSendPricingDialogProps {
  onClose: () => void
  onSuccess: ({ submissionId, data }: { submissionId: string; data: ReSendToIndividualSchemaFormValues }) => void
  submissionId: string
  selectedRecipientType: RecipientType
}

const ReSendIndividual: FC<ReSendPricingDialogProps> = ({
  onClose,
  onSuccess,
  submissionId,
  selectedRecipientType,
}) => {
  const [isCancelConfirmationOpen, setIsCancelConfirmationOpen] = useState(false)
  const { handlePreview, isPreviewFetching } = useGetPriceChangePreview(submissionId)

  // The recipient list is built from every customer in the submission, so this must
  // opt out of the review page's per-page limit.
  // TODO: AK-6472 Check if this is a performance problem for submissions with thousands of customers. If so, we may need to add a new query that returns only the customer IDs.
  const { priceChangeSubmission } = useGetPriceChangeSubmission({
    priceChangeId: submissionId,
    limit: FULL_LIST_LIMIT,
  })

  const customerIds = useMemo(
    () => getPriceChangeGroupsCustomerIds(priceChangeSubmission?.priceChangeItems),
    [priceChangeSubmission?.priceChangeItems]
  )

  const { contacts, customers } = useGetPriceChangeRecipients({
    customerIds,
  })

  const methods = useForm<ReSendToIndividualSchemaFormValues>({
    defaultValues: {
      emailNote: DEFAULT_EMAIL_NOTE,
      recipientType: selectedRecipientType,
      contactId: "",
      customerId: "",
    },
    mode: "onChange",
    resolver: zodResolver(ReSendToIndividualSchema),
  })

  const {
    formState: { isValid, isSubmitted },
  } = methods

  const contactsOptions: Option<string>[] = useMemo(
    () =>
      contacts?.map(
        (contact) =>
          ({
            value: contact.contactId,
            label: contact.contactName,
          }) as Option<string>
      ) ?? [],
    [contacts]
  )

  const customersOptions: Option<string>[] = useMemo(
    () =>
      customers?.map(
        (contact) =>
          ({
            value: contact.companyId,
            label: contact.companyName,
          }) as Option<string>
      ) ?? [],
    [customers]
  )
  const confirmCancel = async () => {
    onClose()
  }

  const handleCancel = () => {
    setIsCancelConfirmationOpen(true)
  }

  const handleSend = (data: ReSendToIndividualSchemaFormValues) => {
    onSuccess({ submissionId, data })
  }

  const handlePreviewClick = () => {
    const values = methods.getValues()

    handlePreview(values.emailNote, {
      companyId: values.recipientType === RecipientType.Customer ? values.customerId : undefined,
      contactId: values.recipientType === RecipientType.Contact ? values.contactId : undefined,
    })
  }

  return (
    <>
      {isCancelConfirmationOpen && (
        <CancelDialog onClose={() => setIsCancelConfirmationOpen(false)} onConfirm={confirmCancel} />
      )}
      <Dialog open onClose={handleCancel} fullWidth maxWidth='lg'>
        <>
          <IconButton aria-label='close' onClick={handleCancel} className='absolute right-4 top-4 text-primary'>
            <CloseOutlined />
          </IconButton>
          <Divider />
          <DialogContent>
            <Box className='flex flex-col items-center'>
              <MailOutlined className='size-10 text-primary' />

              <Typography variant='h6' className='mt-4 text-center text-xl font-normal text-primary'>
                Resend price change notification
              </Typography>

              <Typography variant='body2' className='mt-6 text-base font-normal text-gray-700'>
                This will resend the submitted price change notification to the selected recipient.
              </Typography>
            </Box>

            <FormProvider {...methods}>
              <ReSendIndividualForm contactOptions={contactsOptions} customerOptions={customersOptions} />
              <EmailTemplateForm />
            </FormProvider>
          </DialogContent>
          <SendDialogActions
            submitLabel='Resend'
            isValid={isValid}
            isSubmitted={isSubmitted}
            isPreviewFetching={isPreviewFetching}
            onCancel={handleCancel}
            onPreview={handlePreviewClick}
            onSubmit={methods.handleSubmit(handleSend)}
          />
        </>
      </Dialog>
    </>
  )
}

export default ReSendIndividual
